/**
 * Volba vzhledu aplikace.
 *
 * Volba 'system' se řídí nastavením operačního systému, proto se vedle ní
 * pracuje ještě se skutečně použitým ("efektivním") motivem - ten se zapisuje
 * do atributu `data-bs-theme`, podle kterého se řídí Bootstrap 5.3.
 */
export type ThemeMode = 'light' | 'dark' | 'system'

export type EffectiveTheme = 'light' | 'dark'

/** Musí souhlasit s klíčem ve skriptu v index.html, který motiv nastaví ještě před startem aplikace. */
const KLIC = 'splitup:motiv'

const DOTAZ_NA_SYSTEM = '(prefers-color-scheme: dark)'

/** Barva systémového pruhu prohlížeče (adresní řádek na mobilu, stavový řádek v PWA). */
const BARVA_PRUHU: Record<EffectiveTheme, string> = {
    light: '#2f6fed',
    dark: '#212529',
}

export function readThemeMode(): ThemeMode {
    try {
        const stored = localStorage.getItem(KLIC)
        return stored === 'light' || stored === 'dark' ? stored : 'system'
    } catch {
        return 'system'
    }
}

export function writeThemeMode(mode: ThemeMode): void {
    try {
        // Volba podle systému se neukládá - chybějící záznam znamená právě ji.
        if (mode === 'system') {
            localStorage.removeItem(KLIC)
        } else {
            localStorage.setItem(KLIC, mode)
        }
    } catch {
        // Zakázané úložiště znamená jen to, že se volba nezapamatuje.
    }
}

export function prefersDark(): boolean {
    return window.matchMedia(DOTAZ_NA_SYSTEM).matches
}

/** Ohlásí změnu nastavení motivu v systému; vrací funkci pro odhlášení. */
export function watchSystemTheme(onChange: (dark: boolean) => void): () => void {
    const query = window.matchMedia(DOTAZ_NA_SYSTEM)
    const listener = (event: MediaQueryListEvent) => onChange(event.matches)
    query.addEventListener('change', listener)
    return () => query.removeEventListener('change', listener)
}

export function applyTheme(theme: EffectiveTheme): void {
    document.documentElement.setAttribute('data-bs-theme', theme)
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', BARVA_PRUHU[theme])
}
